import uniq from 'lodash/uniq';

import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { warn } from '@pressreader/logger';
import type { CollectionWithId, IssuePageSet } from '@pressreader/social-types';

import {
    loadIssuePageSetByIdBegan,
    loadIssuePageSetByIdCancelled,
    loadIssuePageSetByIdDone,
    loadIssuePageSetByIdFailed,
    loadIssuePageSetByIssueKeyBegan,
    loadIssuePageSetByIssueKeyCancelled,
    loadIssuePageSetByIssueKeyDone,
    loadIssuePageSetByIssueKeyFailed,
    removePageSetFromCollectionsBegan,
    removePageSetFromCollectionsDone,
    removePageSetFromCollectionsFailed,
    resetIssuePageSetByIdAction,
    resetIssuePageSetByIssueKeyAction,
    updateIssuePageSetByIdBegan,
    updateIssuePageSetByIdDone,
    updateIssuePageSetByIdFailed,
    upsertIssuePageSetCollectionBegan,
    upsertIssuePageSetCollectionDone,
    upsertIssuePageSetCollectionFailed,
} from '../actions';

interface IssuePageSetState {
    status: OperationStatus;
    issueKey: string;
    /**
     * A list of page set ids.
     */
    pageSetIds: string[];
}

interface IssuePageSetItemState {
    status: OperationStatus;
    pageSetId: string;
    pageSet?: IssuePageSet;
}

interface IssuePageSetCollectionsState {
    status: OperationStatus;
    pageSetId: string;
    collections: CollectionWithId[];
}

interface State {
    /**
     * A map of issue key to issue page set state
     */
    issuePageSets: Record<string, IssuePageSetState>;
    /**
     * A map of page set id to page set item state
     */
    pageSet: Record<string, IssuePageSetItemState>;
    pageSetCollections: Record<string, IssuePageSetCollectionsState>;
}

const initialState: State = {
    issuePageSets: {},
    pageSet: {},
    pageSetCollections: {},
};

const reducer = createReducer(
    initialState,
    createActionHandler(loadIssuePageSetByIssueKeyBegan, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                status: OperationStatus.Pending,
                issueKey: payload.issueKey,
                pageSetIds: state.issuePageSets[payload.issueKey]?.pageSetIds || [],
            },
        },
        pageSet: (state.issuePageSets[payload.issueKey]?.pageSetIds || []).reduce(
            (acc, pageSetId) => {
                acc[pageSetId] = {
                    ...state.pageSet[pageSetId],
                    pageSetId,
                    status: OperationStatus.Pending,
                };
                return acc;
            },
            { ...state.pageSet },
        ),
    })),
    createActionHandler(loadIssuePageSetByIssueKeyDone, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                status: OperationStatus.Done,
                issueKey: payload.issueKey,
                pageSetIds: payload.pageSet.map(pageSet => pageSet.id),
            },
        },
        pageSet: payload.pageSet.reduce(
            (acc, pageSet) => {
                acc[pageSet.id] = {
                    ...state.pageSet[pageSet.id],
                    pageSetId: pageSet.id,
                    status: OperationStatus.Done,
                    pageSet,
                };
                return acc;
            },
            { ...state.pageSet },
        ),
    })),
    createActionHandler(loadIssuePageSetByIssueKeyFailed, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                ...state.issuePageSets[payload.issueKey],
                status: OperationStatus.Failed,
                pageSetIds: [],
            },
        },
    })),
    createActionHandler(resetIssuePageSetByIssueKeyAction, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                status: OperationStatus.None,
                issueKey: payload.issueKey,
                pageSetIds: [],
            },
        },
        pageSet: {
            ...state.pageSet,
            ...state.issuePageSets[payload.issueKey]?.pageSetIds.reduce(
                (acc, pageSetId) => {
                    acc[pageSetId] = {
                        ...state.pageSet[pageSetId],
                        pageSetId,
                        status: OperationStatus.None,
                        pageSet: undefined,
                    };
                    return acc;
                },
                { ...state.pageSet },
            ),
        },
    })),
    createActionHandler(resetIssuePageSetByIdAction, (state, { payload }) => ({
        ...state,
        pageSet: {
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                pageSetId: payload.pageSetId,
                status: OperationStatus.None,
            },
        },
    })),
    createActionHandler(loadIssuePageSetByIssueKeyCancelled, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                ...state.issuePageSets[payload.issueKey],
                status: OperationStatus.None,
            },
        },
    })),
    createActionHandler(loadIssuePageSetByIdBegan, (state, { payload }) => ({
        ...state,
        pageSet: {
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                pageSetId: payload.pageSetId,
                status: OperationStatus.Pending,
            },
        },
    })),
    createActionHandler(loadIssuePageSetByIdDone, (state, { payload }) => ({
        ...state,
        issuePageSets: {
            ...state.issuePageSets,
            [payload.issueKey]: {
                status: state.issuePageSets[payload.issueKey]?.status || OperationStatus.None,
                issueKey: payload.issueKey,
                pageSetIds: uniq((state.issuePageSets[payload.issueKey]?.pageSetIds || []).concat(payload.pageSet.map(pageSet => pageSet.id))),
            },
        },
        pageSet: payload.pageSet.reduce(
            (acc, pageSet) => {
                acc[pageSet.id] = {
                    ...state.pageSet[pageSet.id],
                    pageSetId: pageSet.id,
                    status: OperationStatus.Done,
                    pageSet,
                };
                return acc;
            },
            { ...state.pageSet },
        ),
    })),
    createActionHandler(loadIssuePageSetByIdFailed, (state, { payload }) => ({
        ...state,
        pageSet: {
            ...state.pageSet,
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                pageSet: undefined,
                status: OperationStatus.Failed,
            },
        },
    })),
    createActionHandler(loadIssuePageSetByIdCancelled, (state, { payload }) => ({
        ...state,
        pageSet: {
            ...state.pageSet,
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                status: OperationStatus.None,
            },
        },
    })),
    createActionHandler(upsertIssuePageSetCollectionBegan, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                status: OperationStatus.Pending,
                pageSetId: payload.pageSetId,
                collections: [],
            },
        },
    })),
    createActionHandler(upsertIssuePageSetCollectionDone, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                status: OperationStatus.Done,
                pageSetId: payload.pageSetId,
                collections: payload.collections,
            },
        },
    })),
    createActionHandler(upsertIssuePageSetCollectionFailed, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                status: OperationStatus.Failed,
                pageSetId: payload.pageSetId,
                collections: [],
            },
        },
    })),
    createActionHandler(removePageSetFromCollectionsBegan, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                ...state.pageSetCollections[payload.pageSetId],
                status: OperationStatus.Pending,
            },
        },
        issuePageSets: Object.keys(state.issuePageSets).reduce(
            (acc, issueKey) => {
                if (state.issuePageSets[issueKey].pageSetIds.findIndex(id => id !== payload.pageSetId) !== -1) {
                    acc[issueKey] = {
                        ...state.issuePageSets[issueKey],
                        status: OperationStatus.Pending,
                    };
                }
                return acc;
            },
            { ...state.issuePageSets },
        ),
    })),
    createActionHandler(removePageSetFromCollectionsDone, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                status: OperationStatus.Done,
                pageSetId: payload.pageSetId,
                collections: [],
            },
        },
        issuePageSets: Object.keys(state.issuePageSets).reduce(
            (acc, issueKey) => {
                acc[issueKey] = {
                    ...state.issuePageSets[issueKey],
                    pageSetIds: state.issuePageSets[issueKey].pageSetIds.filter(id => id !== payload.pageSetId),
                };
                return acc;
            },
            { ...state.issuePageSets },
        ),
    })),
    createActionHandler(removePageSetFromCollectionsFailed, (state, { payload }) => ({
        ...state,
        pageSetCollections: {
            ...state.pageSetCollections,
            [payload.pageSetId]: {
                ...state.pageSetCollections[payload.pageSetId],
                status: OperationStatus.Failed,
            },
        },
    })),
    createActionHandler(updateIssuePageSetByIdBegan, (state, { payload }) => ({
        ...state,
        pageSet: {
            ...state.pageSet,
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                pageSetId: payload.pageSetId,
                status: OperationStatus.Pending,
            },
        },
    })),
    createActionHandler(updateIssuePageSetByIdDone, (state, { payload }) => {
        const pageSetCurrent = state.pageSet[payload.pageSetId].pageSet;

        if (!pageSetCurrent) {
            warn(`Page set not found by id ${payload.pageSetId}`);
        }

        return {
            ...state,
            pageSet: {
                ...state.pageSet,
                [payload.pageSetId]: {
                    ...state.pageSet[payload.pageSetId],
                    status: OperationStatus.Done,
                    pageSetId: payload.pageSetId,
                    pageSet: pageSetCurrent
                        ? {
                              ...pageSetCurrent,
                              id: payload.pageSetId,
                              pages: payload.pages,
                          }
                        : undefined,
                },
            },
        };
    }),
    createActionHandler(updateIssuePageSetByIdFailed, (state, { payload }) => ({
        ...state,
        pageSet: {
            ...state.pageSet,
            [payload.pageSetId]: {
                ...state.pageSet[payload.pageSetId],
                status: OperationStatus.Failed,
            },
        },
    })),
);

export type { IssuePageSetCollectionsState, IssuePageSetItemState, IssuePageSetState, State };
export { reducer };
